<template>
  <section class="bg-white p-3 border border-gray-200 rounded-md">
    <div class="flex items-center gap-1.5 mb-2">
      <i class="pi pi-book text-primary text-sm"></i>
      <h3 class="font-semibold text-sm text-gray-700">{{ labels.CLASS }} Information</h3>
    </div>

    <!-- Current enrollment -->
    <div v-if="student?.currentClassroomSection" class="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
      <p><strong>{{ labels.CLASS }}:</strong> {{ student.currentClassroomSection.classroom?.name || 'N/A' }}</p>
      <p v-if="student.currentClassroomSection.section?.name"><strong>Section:</strong> {{ student.currentClassroomSection.section.name }}</p>
    </div>

    <!-- Not enrolled in current calendar — show last record -->
    <div v-else>
      <div class="flex items-center gap-1.5 px-2 py-1.5 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800 mb-2">
        <i class="pi pi-exclamation-triangle text-amber-600 text-xs"></i>
        <span>Not enrolled in the current academic calendar.</span>
      </div>

      <div v-if="lastRecord" class="bg-gray-50 border border-gray-200 rounded p-2 text-xs space-y-1">
        <p class="font-semibold text-gray-700 text-[11px] uppercase tracking-wide mb-1">Last Active Record</p>
        <p><strong>Calendar:</strong> {{ lastCalendarLabel }}</p>
        <p v-if="lastRecord.classroomSection?.classroom?.name">
          <strong>{{ labels.CLASS }}:</strong> {{ lastRecord.classroomSection.classroom.name }}
          <span v-if="lastRecord.classroomSection.section?.name"> — {{ lastRecord.classroomSection.section.name }}</span>
        </p>
        <p v-if="lastRecord.programme?.program_name"><strong>Programme:</strong> {{ lastRecord.programme.program_name }}</p>
        <p v-if="lastRecord.status"><strong>Status:</strong> <span class="capitalize">{{ lastRecord.status }}</span></p>
        <p class="text-[11px] text-gray-500 pt-1 italic">Use Promotion to enrol this student in the current calendar, or switch to the calendar above.</p>
      </div>

      <div v-else class="text-xs text-gray-500">
        No academic records found for this student.
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import type { Student } from '@/types/composition-functions';
import { useSchoolLabels } from '@/composables/useSchoolLabels';

const { labels } = useSchoolLabels();

const props = defineProps<{
  student: Student | null
}>();

const lastRecord = computed(() => props.student?.lastAcademicRecord ?? null);

const lastCalendarLabel = computed(() => {
  if (!lastRecord.value?.academicCalendar) return 'N/A';
  const cal = lastRecord.value.academicCalendar;
  const session = cal.session?.name || '';
  const term = cal.termStructure?.name || '';
  return `${session}${term ? ' — ' + term : ''}`;
});
</script>
